import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSummary, clearSummaryStatus, startSummaryPolling, stopSummaryPolling } from '../store/slices/chatSlice';
import './SummaryModal.css';

const SummaryModal = () => {
  const dispatch = useDispatch();
  const { summary, summarizing, summaryStatus, currentConversationId } = useSelector((state) => state.chat);

  useEffect(() => {
    // Start polling when modal opens and we have a conversation ID
    if (currentConversationId && (summarizing || summary)) {
      dispatch(startSummaryPolling(currentConversationId));
    }

    // Cleanup polling when component unmounts
    return () => {
      dispatch(stopSummaryPolling());
    };
  }, [dispatch, currentConversationId, summarizing, summary]);

  const handleClose = () => {
    dispatch(stopSummaryPolling());
    dispatch(clearSummary());
    dispatch(clearSummaryStatus());
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!summary && !summarizing && !summaryStatus) {
    return null;
  }

  const renderVideoSection = () => {
    if (!summaryStatus?.video_summary) {
      return (
        <div className="summary-video">
          <h4>Video Summary</h4>
          <div className="video-loading">
            <div className="video-loading-spinner"></div>
            <p>Waiting for video summary...</p>
          </div>
        </div>
      );
    }

    const { video_summary } = summaryStatus;
    
    if (video_summary.status === 'processing') {
      return (
        <div className="summary-video">
          <h4>Video Summary</h4>
          <div className="video-loading">
            <div className="video-loading-spinner"></div>
            <p>Processing video summary...</p>
          </div>
        </div>
      );
    }

    if (video_summary.status === 'completed' && video_summary.latest_video) {
      const { latest_video } = video_summary;
      return (
        <div className="summary-video">
          <h4>Video Summary</h4>
          <div className="video-content">
            <div className="video-thumbnail">
              <img src={latest_video.thumbnail_url} alt="Video thumbnail" />
              <div className="video-overlay">
                <button 
                  className="play-button"
                  onClick={() => window.open(latest_video.video_url, '_blank')}
                >
                  ▶
                </button>
              </div>
            </div>
            <div className="video-info">
              <h5>{latest_video.title}</h5>
              <div className="video-actions">
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => window.open(latest_video.video_url, '_blank')}
                >
                  Watch Video
                </button>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => window.open(latest_video.share_page_url, '_blank')}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="summary-video">
        <h4>Video Summary</h4>
        <div className="video-loading">
          <div className="video-loading-spinner"></div>
          <p>Preparing video summary...</p>
        </div>
      </div>
    );
  };

  const renderTextSummary = () => {
    if (!summaryStatus?.text_summary) {
      return (
        <div className="summary-text">
          <h4>Text Summary</h4>
          <div className="text-loading">
            <div className="loading-spinner"></div>
            <p>Waiting for text summary...</p>
          </div>
        </div>
      );
    }

    const { text_summary } = summaryStatus;
    
    if (text_summary.status === 'processing') {
      return (
        <div className="summary-text">
          <h4>Text Summary</h4>
          <div className="text-loading">
            <div className="loading-spinner"></div>
            <p>Processing text summary...</p>
          </div>
        </div>
      );
    }

    if (text_summary.status === 'completed' && text_summary.summary) {
      return (
        <div className="summary-text">
          <h4>Text Summary</h4>
          <p>{text_summary.summary}</p>
          {text_summary.metadata && (
            <div className="summary-metadata">
              <small>
                Generated with {text_summary.metadata.provider} ({text_summary.metadata.model})
              </small>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="summary-text">
        <h4>Text Summary</h4>
        <div className="text-loading">
          <div className="loading-spinner"></div>
          <p>Preparing text summary...</p>
        </div>
      </div>
    );
  };

  return (
    <div className="summary-modal-overlay" onClick={handleOverlayClick}>
      <div className="summary-modal">
        <div className="summary-modal-header">
          <h3>Conversation Summary</h3>
          <button className="summary-modal-close" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        <div className="summary-modal-content">
          {summarizing ? (
            <div className="summary-loading">
              <div className="loading-spinner"></div>
              <p>Initiating summary generation...</p>
              <small>This may take a few moments</small>
            </div>
          ) : (
            <div className="summary-content">
              {renderTextSummary()}
              {renderVideoSection()}
              
              {summaryStatus?.overall_status === 'completed' && (
                <div className="summary-complete">
                  <div className="success-indicator">✓ Summary Complete</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryModal; 